<template>
  <div class="homepage">
    <!-- 悬浮按钮容器 -->
    <div class="floating-actions">
      <button class="floating-btn edit-mode-toggle" :class="{ active: isEditMode }" @click="toggleEditMode" :title="isEditMode ? '完成编辑' : '编辑链接'">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
        </svg>
      </button>
      <button class="floating-btn add-group-btn" @click="showAddGroupModal = true" title="添加组">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
      </button>
    </div>

    <div class="container">

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>加载中...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <svg class="error-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
        <p>{{ error }}</p>
        <button class="retry-button" @click="fetchData">重试</button>
      </div>

      <div v-else-if="groups.length === 0" class="empty-state">
        <svg class="empty-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
        <p>暂无链接</p>
      </div>

      <main v-else class="groups">
        <section v-for="group in sortedGroups" :key="group.group_id" class="group-section">
          <div class="group-header">
            <h2 class="group-title">{{ group.group_name }}</h2>
            <div class="group-actions">
              <button class="add-link-btn" @click="openAddLinkModal(group)" :aria-label="'添加链接到' + group.group_name">
                <span class="btn-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </span>
              </button>
              <button class="edit-group-btn" @click="openEditGroupModal(group)" :aria-label="'编辑' + group.group_name">
                <span class="btn-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                  </svg>
                </span>
              </button>
              <button class="delete-group-btn" @click="confirmDeleteGroup(group)" :aria-label="'删除' + group.group_name">
                <span class="btn-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                  </svg>
                </span>
              </button>
            </div>
          </div>
          <div class="links-grid">
            <div
              v-for="(link, index) in sortedLinks(group.links)"
              :key="link.id || `${link.link_url}-${index}`"
              class="link-card-wrapper"
            >
              <a
                :href="link.link_url"
                target="_blank"
                rel="noopener noreferrer"
                class="link-card"
                :class="{ 'edit-mode-active': isEditMode }"
              >
                <div class="link-icon">
                  <img
                    v-if="link.icon_link"
                    :src="link.icon_link"
                    :alt="link.link_name"
                    class="icon-image"
                    loading="lazy"
                    @error="handleImageError"
                  />
                  <svg v-else class="icon-placeholder" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                  </svg>
                </div>
                <span class="link-name">{{ link.link_name }}</span>
              </a>
              <div v-if="isEditMode" class="link-actions">
                <button class="link-edit-btn" @click="openEditLinkModal(group, link)" :aria-label="'编辑' + link.link_name">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                  </svg>
                </button>
                <button class="link-delete-btn" @click="confirmDeleteLink(group, link)" :aria-label="'删除' + link.link_name">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>

    <!-- 删除组确认模态框 -->
    <div v-if="showDeleteConfirmModal" class="modal-overlay" @click.self="cancelDeleteGroup">
      <div class="modal-content">
        <h3 class="modal-title">删除组</h3>
        <p class="delete-confirm-message">确定要删除组"{{ groupToDelete?.group_name }}"吗？此操作不可撤销。</p>
        <div class="modal-actions">
          <button class="btn-cancel" @click="cancelDeleteGroup">取消</button>
          <button class="btn-delete" @click="handleDeleteGroup" :disabled="isDeleting">
            {{ isDeleting ? '删除中...' : '确认删除' }}
          </button>
        </div>
        <p v-if="deleteError" class="error-message">{{ deleteError }}</p>
      </div>
    </div>

    <!-- 添加组模态框 -->
    <div v-if="showAddGroupModal" class="modal-overlay" @click.self="showAddGroupModal = false">
      <div class="modal-content">
        <h3 class="modal-title">添加新组</h3>
        <form @submit.prevent="handleAddGroup" class="add-group-form">
          <div class="form-field">
            <label for="groupName">组名称</label>
            <input
              id="groupName"
              v-model="newGroupName"
              type="text"
              placeholder="输入组名称"
              required
              maxlength="50"
            />
          </div>
          <div class="modal-actions">
            <button type="button" class="btn-cancel" @click="showAddGroupModal = false">取消</button>
            <button type="submit" class="btn-confirm" :disabled="isSubmitting">
              {{ isSubmitting ? '创建中...' : '确认' }}
            </button>
          </div>
        </form>
        <p v-if="groupError" class="error-message">{{ groupError }}</p>
      </div>
    </div>

    <!-- 编辑组模态框 -->
    <div v-if="showEditGroupModal" class="modal-overlay" @click.self="closeEditGroupModal">
      <div class="modal-content">
        <h3 class="modal-title">编辑组</h3>
        <form @submit.prevent="handleEditGroup" class="add-group-form">
          <div class="form-field">
            <label for="editGroupName">组名称</label>
            <input
              id="editGroupName"
              v-model="editingGroupName"
              type="text"
              placeholder="输入组名称"
              required
              maxlength="50"
            />
          </div>
          <div class="modal-actions">
            <button type="button" class="btn-cancel" @click="closeEditGroupModal">取消</button>
            <button type="submit" class="btn-confirm" :disabled="isEditing">
              {{ isEditing ? '保存中...' : '保存' }}
            </button>
          </div>
        </form>
        <p v-if="editGroupError" class="error-message">{{ editGroupError }}</p>
      </div>
    </div>

    <!-- 添加链接模态框 -->
    <div v-if="showAddLinkModal" class="modal-overlay" @click.self="closeAddLinkModal">
      <div class="modal-content">
        <h3 class="modal-title">添加链接</h3>
        <form @submit.prevent="handleAddLink" class="add-group-form">
          <div class="form-field">
            <label for="linkName">链接名称</label>
            <input
              id="linkName"
              v-model="newLink.link_name"
              type="text"
              placeholder="输入链接名称"
              required
              maxlength="50"
            />
          </div>
          <div class="form-field">
            <label for="linkUrl">链接地址</label>
            <input
              id="linkUrl"
              v-model="newLink.link_url"
              type="url"
              placeholder="https://example.com"
              required
            />
          </div>
          <div class="form-field">
            <label for="iconLink">图标链接（可选）</label>
            <input
              id="iconLink"
              v-model="newLink.icon_link"
              type="url"
              placeholder="https://example.com/icon.png"
            />
          </div>
          <div class="modal-actions">
            <button type="button" class="btn-cancel" @click="closeAddLinkModal">取消</button>
            <button type="submit" class="btn-confirm" :disabled="isAddingLink">
              {{ isAddingLink ? '添加中...' : '确认' }}
            </button>
          </div>
        </form>
        <p v-if="addLinkError" class="error-message">{{ addLinkError }}</p>
      </div>
    </div>

    <!-- 编辑链接模态框 -->
    <div v-if="showEditLinkModal" class="modal-overlay" @click.self="closeEditLinkModal">
      <div class="modal-content">
        <h3 class="modal-title">编辑链接</h3>
        <form @submit.prevent="handleEditLink" class="add-group-form">
          <div class="form-field">
            <label for="editLinkName">链接名称</label>
            <input
              id="editLinkName"
              v-model="editingLink.link_name"
              type="text"
              placeholder="输入链接名称"
              required
              maxlength="50"
            />
          </div>
          <div class="form-field">
            <label for="editLinkUrl">链接地址</label>
            <input
              id="editLinkUrl"
              v-model="editingLink.link_url"
              type="url"
              placeholder="https://example.com"
              required
            />
          </div>
          <div class="form-field">
            <label for="editIconLink">图标链接（可选）</label>
            <input
              id="editIconLink"
              v-model="editingLink.icon_link"
              type="url"
              placeholder="https://example.com/icon.png"
            />
          </div>
          <div class="modal-actions">
            <button type="button" class="btn-cancel" @click="closeEditLinkModal">取消</button>
            <button type="submit" class="btn-confirm" :disabled="isEditingLink">
              {{ isEditingLink ? '保存中...' : '保存' }}
            </button>
          </div>
        </form>
        <p v-if="editLinkError" class="error-message">{{ editLinkError }}</p>
      </div>
    </div>

    <!-- 删除链接确认模态框 -->
    <div v-if="showDeleteLinkModal" class="modal-overlay" @click.self="cancelDeleteLink">
      <div class="modal-content">
        <h3 class="modal-title">删除链接</h3>
        <p class="delete-confirm-message">确定要删除链接"{{ linkToDelete?.link_name }}"吗？此操作不可撤销。</p>
        <div class="modal-actions">
          <button class="btn-cancel" @click="cancelDeleteLink">取消</button>
          <button class="btn-delete" @click="handleDeleteLink" :disabled="isDeletingLink">
            {{ isDeletingLink ? '删除中...' : '确认删除' }}
          </button>
        </div>
        <p v-if="deleteLinkError" class="error-message">{{ deleteLinkError }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'

const API_BASE = 'https://hd-api.wuhd.xyz'

interface Link {
  icon_link: string
  link_name: string
  link_url: string
  sort_index: number
  link_id?: number
  id?: number
}

interface Group {
  group_id: number
  group_name: string
  sort_index: number
  links: Link[]
}

interface ApiResponse {
  user_id: string
  groups: Group[]
}

interface CreateGroupResponse {
  message: string
  data: {
    id: number
    group_name: string
    user_id: string
    sort_index: number
  }
}

interface CreateLinkResponse {
  message: string
  data: {
    id: number
    icon_link: string
    link_name: string
    link_url: string
    sort_index: number
    group_id: number
  }
}

const userid = ref('default')
const displayName = ref('个人主页')
const groups = ref<Group[]>([])
const loading = ref(true)
const error = ref('')

const showAddGroupModal = ref(false)
const newGroupName = ref('')
const isSubmitting = ref(false)
const groupError = ref('')

const showDeleteConfirmModal = ref(false)
const groupToDelete = ref<Group | null>(null)
const isDeleting = ref(false)
const deleteError = ref('')

const showEditGroupModal = ref(false)
const groupToEdit = ref<Group | null>(null)
const editingGroupName = ref('')
const isEditing = ref(false)
const editGroupError = ref('')

const showAddLinkModal = ref(false)
const groupForLink = ref<Group | null>(null)
const newLink = ref({
  link_name: '',
  link_url: '',
  icon_link: ''
})
const isAddingLink = ref(false)
const addLinkError = ref('')

const isEditMode = ref(false)

const showEditLinkModal = ref(false)
const editingLink = ref({
  link_name: '',
  link_url: '',
  icon_link: ''
})
const editingLinkId = ref<number | null>(null)
const groupForEditLink = ref<Group | null>(null)
const isEditingLink = ref(false)
const editLinkError = ref('')

const showDeleteLinkModal = ref(false)
const linkToDelete = ref<Link | null>(null)
const groupForDeleteLink = ref<Group | null>(null)
const isDeletingLink = ref(false)
const deleteLinkError = ref('')

const sortedGroups = computed(() => {
  return [...groups.value].sort((a, b) => a.sort_index - b.sort_index)
})

const sortedLinks = (links: Link[]) => {
  return [...links].sort((a, b) => a.sort_index - b.sort_index)
}

const toggleEditMode = () => {
  isEditMode.value = !isEditMode.value
}

const getUserId = (): string => {
  const params = new URLSearchParams(window.location.search)
  return params.get('userid') || 'default'
}

const fetchData = async () => {
  loading.value = true
  error.value = ''

  try {
    const response = await fetch(`${API_BASE}/homepage/links/${userid.value}`)
    if (!response.ok) {
      throw new Error('获取数据失败')
    }
    const data: ApiResponse = await response.json()
    groups.value = data.groups || []
    displayName.value = data.user_id === 'default' ? '个人主页' : data.user_id
  } catch (err) {
    error.value = '加载失败，请稍后重试'
  } finally {
    loading.value = false
  }
}

const handleAddGroup = async () => {
  if (!newGroupName.value.trim()) {
    groupError.value = '请输入组名称'
    return
  }

  isSubmitting.value = true
  groupError.value = ''

  try {
    const response = await fetch(`${API_BASE}/group`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        group_name: newGroupName.value.trim(),
        user_id: userid.value,
        sort_index: groups.value.length
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || '创建组失败')
    }

    const result: CreateGroupResponse = await response.json()

    groups.value.push({
      group_id: result.data.id,
      group_name: result.data.group_name,
      sort_index: result.data.sort_index,
      links: []
    })

    newGroupName.value = ''
    showAddGroupModal.value = false
  } catch (err) {
    groupError.value = err instanceof Error ? err.message : '创建组失败，请稍后重试'
  } finally {
    isSubmitting.value = false
  }
}

const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement
  target.style.display = 'none'
}

const confirmDeleteGroup = (group: Group) => {
  groupToDelete.value = group
  showDeleteConfirmModal.value = true
  deleteError.value = ''
}

const cancelDeleteGroup = () => {
  groupToDelete.value = null
  showDeleteConfirmModal.value = false
  deleteError.value = ''
}

const handleDeleteGroup = async () => {
  if (!groupToDelete.value) return

  isDeleting.value = true
  deleteError.value = ''

  try {
    const response = await fetch(`${API_BASE}/group/${groupToDelete.value.group_id}`, {
      method: 'DELETE'
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: '删除组失败' }))
      throw new Error(errorData.message || '删除组失败')
    }

    groups.value = groups.value.filter(g => g.group_id !== groupToDelete.value?.group_id)
    cancelDeleteGroup()
  } catch (err) {
    deleteError.value = err instanceof Error ? err.message : '删除组失败，请稍后重试'
  } finally {
    isDeleting.value = false
  }
}

const openEditGroupModal = (group: Group) => {
  groupToEdit.value = group
  editingGroupName.value = group.group_name
  editGroupError.value = ''
  showEditGroupModal.value = true
}

const closeEditGroupModal = () => {
  groupToEdit.value = null
  editingGroupName.value = ''
  editGroupError.value = ''
  showEditGroupModal.value = false
}

const handleEditGroup = async () => {
  if (!groupToEdit.value || !editingGroupName.value.trim()) {
    editGroupError.value = '请输入组名称'
    return
  }

  isEditing.value = true
  editGroupError.value = ''

  try {
    const response = await fetch(`${API_BASE}/group/${groupToEdit.value.group_id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        group_name: editingGroupName.value.trim()
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || '更新组失败')
    }

    const group = groups.value.find(g => g.group_id === groupToEdit.value?.group_id)
    if (group) {
      group.group_name = editingGroupName.value.trim()
    }

    closeEditGroupModal()
  } catch (err) {
    editGroupError.value = err instanceof Error ? err.message : '更新组失败，请稍后重试'
  } finally {
    isEditing.value = false
  }
}

const openAddLinkModal = (group: Group) => {
  groupForLink.value = group
  newLink.value = {
    link_name: '',
    link_url: '',
    icon_link: ''
  }
  addLinkError.value = ''
  showAddLinkModal.value = true
}

const closeAddLinkModal = () => {
  groupForLink.value = null
  newLink.value = {
    link_name: '',
    link_url: '',
    icon_link: ''
  }
  addLinkError.value = ''
  showAddLinkModal.value = false
}

const handleAddLink = async () => {
  if (!groupForLink.value) return

  if (!newLink.value.link_name.trim() || !newLink.value.link_url.trim()) {
    addLinkError.value = '请输入链接名称和地址'
    return
  }

  isAddingLink.value = true
  addLinkError.value = ''

  try {
    const response = await fetch(`${API_BASE}/link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        icon_link: newLink.value.icon_link.trim() || null,
        link_name: newLink.value.link_name.trim(),
        link_url: newLink.value.link_url.trim(),
        group_id: groupForLink.value.group_id,
        sort_index: groupForLink.value.links.length
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || '添加链接失败')
    }

    const result: CreateLinkResponse = await response.json()

    const group = groups.value.find(g => g.group_id === groupForLink.value?.group_id)
    if (group) {
      group.links.push({
        icon_link: result.data.icon_link,
        link_name: result.data.link_name,
        link_url: result.data.link_url,
        sort_index: result.data.sort_index,
        id: result.data.id
      })
    }

    closeAddLinkModal()
  } catch (err) {
    addLinkError.value = err instanceof Error ? err.message : '添加链接失败，请稍后重试'
  } finally {
    isAddingLink.value = false
  }
}

const openEditLinkModal = (group: Group, link: Link) => {
  const linkId = link.id || link.link_id
  groupForEditLink.value = group
  editingLinkId.value = linkId || null
  editingLink.value = {
    link_name: link.link_name,
    link_url: link.link_url,
    icon_link: link.icon_link
  }
  editLinkError.value = ''
  showEditLinkModal.value = true
}

const closeEditLinkModal = () => {
  groupForEditLink.value = null
  editingLinkId.value = null
  editingLink.value = {
    link_name: '',
    link_url: '',
    icon_link: ''
  }
  editLinkError.value = ''
  showEditLinkModal.value = false
}

const handleEditLink = async () => {
  const linkId = editingLinkId.value
  if (!linkId) {
    editLinkError.value = '链接ID缺失，无法编辑'
    return
  }

  if (!editingLink.value.link_name.trim() || !editingLink.value.link_url.trim()) {
    editLinkError.value = '请输入链接名称和地址'
    return
  }

  isEditingLink.value = true
  editLinkError.value = ''

  try {
    const response = await fetch(`${API_BASE}/link/${linkId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        link_name: editingLink.value.link_name.trim(),
        link_url: editingLink.value.link_url.trim(),
        icon_link: editingLink.value.icon_link?.trim() || null
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || '更新链接失败')
    }

    const group = groups.value.find(g => g.group_id === groupForEditLink.value?.group_id)
    if (group) {
      const link = group.links.find(l => l.id === linkId || l.link_id === linkId)
      if (link) {
        link.link_name = editingLink.value.link_name.trim()
        link.link_url = editingLink.value.link_url.trim()
        link.icon_link = editingLink.value.icon_link?.trim() || ''
      }
    }

    closeEditLinkModal()
  } catch (err) {
    editLinkError.value = err instanceof Error ? err.message : '更新链接失败，请稍后重试'
  } finally {
    isEditingLink.value = false
  }
}

const confirmDeleteLink = (group: Group, link: Link) => {
  const linkId = link.id || link.link_id
  groupForDeleteLink.value = group
  linkToDelete.value = link
  showDeleteLinkModal.value = true
  deleteLinkError.value = ''
}

const cancelDeleteLink = () => {
  groupForDeleteLink.value = null
  linkToDelete.value = null
  showDeleteLinkModal.value = false
  deleteLinkError.value = ''
}

const handleDeleteLink = async () => {
  const linkId = linkToDelete.value?.id || linkToDelete.value?.link_id
  if (!linkId) {
    deleteLinkError.value = '链接ID缺失，无法删除'
    return
  }

  isDeletingLink.value = true
  deleteLinkError.value = ''

  try {
    const response = await fetch(`${API_BASE}/link/${linkId}`, {
      method: 'DELETE'
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: '删除链接失败' }))
      throw new Error(errorData.message || '删除链接失败')
    }

    const group = groups.value.find(g => g.group_id === groupForDeleteLink.value?.group_id)
    if (group) {
      group.links = group.links.filter(l => l.id !== linkId && l.link_id !== linkId)
    }

    cancelDeleteLink()
  } catch (err) {
    deleteLinkError.value = err instanceof Error ? err.message : '删除链接失败，请稍后重试'
  } finally {
    isDeletingLink.value = false
  }
}

onMounted(() => {
  userid.value = getUserId()
  fetchData()
})
</script>

<style scoped>
.homepage {
  min-height: 100vh;
  padding: var(--spacing-xl) var(--spacing-md);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

/* 悬浮按钮容器 */
.floating-actions {
  position: fixed;
  top: var(--spacing-lg);
  left: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  z-index: 100;
}

/* 悬浮按钮基础样式 */
.floating-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  border-radius: var(--radius-full);
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  transition: all var(--transition-normal);
}

.floating-btn svg {
  width: 22px;
  height: 22px;
  transition: transform var(--transition-normal);
}

/* 编辑链接悬浮按钮 */
.floating-btn.edit-mode-toggle {
  background: var(--glass-bg);
  backdrop-filter: blur(10px);
  color: var(--color-text-secondary);
  border: 1px solid var(--glass-border);
}

.floating-btn.edit-mode-toggle:hover {
  background: rgba(99, 102, 241, 0.15);
  border-color: rgba(99, 102, 241, 0.3);
  color: var(--color-cta-mid);
  transform: scale(1.1);
  box-shadow:
    0 6px 24px rgba(99, 102, 241, 0.25),
    0 0 0 1px rgba(99, 102, 241, 0.2) inset;
}

.floating-btn.edit-mode-toggle:hover svg {
  transform: rotate(-10deg) scale(1.1);
}

.floating-btn.edit-mode-toggle.active {
  background: var(--gradient-primary);
  background-size: 200% 200%;
  color: white;
  border-color: transparent;
  box-shadow:
    0 6px 24px rgba(99, 102, 241, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.2) inset;
}

.floating-btn.edit-mode-toggle.active:hover {
  background-position: 100% 0;
  transform: scale(1.1);
}

.floating-btn.edit-mode-toggle:focus {
  outline: none;
  box-shadow:
    0 6px 24px rgba(99, 102, 241, 0.25),
    0 0 0 3px rgba(99, 102, 241, 0.2);
}

/* 添加组悬浮按钮 */
.floating-btn.add-group-btn {
  background: var(--gradient-primary);
  background-size: 200% 200%;
  color: white;
  border: none;
}

.floating-btn.add-group-btn:hover {
  transform: scale(1.1) rotate(90deg);
  box-shadow:
    0 8px 28px rgba(99, 102, 241, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.15) inset;
  background-position: 100% 0;
}

.floating-btn.add-group-btn:hover svg {
  transform: rotate(90deg);
}

.floating-btn.add-group-btn:active {
  transform: scale(1.05);
}

.floating-btn.add-group-btn:focus {
  outline: none;
  box-shadow:
    0 8px 28px rgba(99, 102, 241, 0.4),
    0 0 0 3px rgba(99, 102, 241, 0.2);
}


.loading-state,
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-2xl) var(--spacing-md);
  text-align: center;
  color: var(--color-text-secondary);
}

.spinner {
  width: 44px;
  height: 44px;
  position: relative;
}

.spinner::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: var(--color-cta-mid);
  animation: spin 0.8s linear infinite;
}

.spinner::after {
  content: '';
  position: absolute;
  inset: 6px;
  border-radius: 50%;
  border: 2px solid transparent;
  border-top-color: var(--color-cta-end);
  animation: spin 0.6s linear infinite reverse;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-icon,
.empty-icon {
  width: 64px;
  height: 64px;
  color: var(--color-text-muted);
  margin-bottom: var(--spacing-md);
  opacity: 0.6;
}

.retry-button {
  margin-top: var(--spacing-lg);
  padding: 0.875rem 2rem;
  background: var(--gradient-primary);
  background-size: 200% 200%;
  color: white;
  border-radius: var(--radius-lg);
  font-family: 'Outfit', sans-serif;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
  transition: all var(--transition-normal);
}

.retry-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(99, 102, 241, 0.4);
  background-position: 100% 0;
}

.retry-button:focus {
  outline: none;
  box-shadow:
    0 8px 24px rgba(99, 102, 241, 0.4),
    0 0 0 3px rgba(99, 102, 241, 0.2);
}

.groups {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2xl);
}

.group-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
}

.group-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

/* 添加链接按钮 - 玻璃态效果 */
.add-link-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: var(--glass-bg);
  backdrop-filter: blur(10px);
  color: var(--color-cta-mid);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: var(--radius-md);
  transition: all var(--transition-normal);
  flex-shrink: 0;
}

.add-link-btn svg {
  width: 18px;
  height: 18px;
  transition: all var(--transition-normal);
}

.add-link-btn:hover {
  background: rgba(99, 102, 241, 0.15);
  border-color: rgba(99, 102, 241, 0.4);
  color: var(--color-cta-end);
  transform: scale(1.05) rotate(90deg);
  box-shadow: 0 0 20px rgba(99, 102, 241, 0.25);
}

.add-link-btn:hover svg {
  transform: rotate(90deg);
}

.add-link-btn:focus {
  outline: none;
  box-shadow:
    0 0 0 3px rgba(99, 102, 241, 0.2),
    0 0 20px rgba(99, 102, 241, 0.15);
}

/* 编辑按钮 - 玻璃态效果 */
.edit-group-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: var(--glass-bg);
  backdrop-filter: blur(10px);
  color: var(--color-text-secondary);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-md);
  transition: all var(--transition-normal);
  flex-shrink: 0;
}

.edit-group-btn svg {
  width: 18px;
  height: 18px;
  transition: all var(--transition-normal);
}

.edit-group-btn:hover {
  background: rgba(99, 102, 241, 0.15);
  border-color: rgba(99, 102, 241, 0.3);
  color: var(--color-cta-mid);
  transform: scale(1.05);
  box-shadow: 0 0 20px rgba(99, 102, 241, 0.2);
}

.edit-group-btn:hover svg {
  transform: rotate(-10deg) scale(1.1);
}

.edit-group-btn:focus {
  outline: none;
  box-shadow:
    0 0 0 3px rgba(99, 102, 241, 0.2),
    0 0 20px rgba(99, 102, 241, 0.15);
}

/* 组标题样式 */
.group-title {
  font-family: 'Outfit', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text);
  padding-bottom: var(--spacing-sm);
  border-bottom: 1px solid var(--color-divider);
  flex: 1;
  letter-spacing: -0.02em;
}

/* 删除按钮 - 危险操作 */
.delete-group-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: var(--glass-bg);
  backdrop-filter: blur(10px);
  color: var(--color-text-secondary);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-md);
  transition: all var(--transition-normal);
  flex-shrink: 0;
}

.delete-group-btn svg {
  width: 18px;
  height: 18px;
  transition: all var(--transition-normal);
}

.delete-group-btn:hover {
  background: var(--color-danger-subtle);
  border-color: var(--color-danger);
  color: var(--color-danger);
  transform: scale(1.05);
  box-shadow: 0 0 20px rgba(239, 68, 68, 0.2);
}

.delete-group-btn:hover svg {
  transform: scale(1.1);
}

.delete-group-btn:focus {
  outline: none;
  box-shadow:
    0 0 0 3px rgba(239, 68, 68, 0.2),
    0 0 20px rgba(239, 68, 68, 0.15);
}

.links-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: var(--spacing-md);
}

.link-card-wrapper {
  position: relative;
}

.link-actions {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  gap: 4px;
  z-index: 10;
}

.link-edit-btn,
.link-delete-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  transition: all var(--transition-normal);
  flex-shrink: 0;
}

.link-edit-btn svg,
.link-delete-btn svg {
  width: 14px;
  height: 14px;
}

.link-edit-btn:hover {
  background: rgba(99, 102, 241, 0.15);
  border-color: var(--color-cta-mid);
  color: var(--color-cta-mid);
  transform: scale(1.1);
}

.link-delete-btn:hover {
  background: var(--color-danger-subtle);
  border-color: var(--color-danger);
  color: var(--color-danger);
  transform: scale(1.1);
}

.link-card.edit-mode-active {
  padding-top: calc(var(--spacing-md) + 16px);
}

@media (min-width: 640px) {
  .links-grid {
    grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
    gap: var(--spacing-lg);
  }
}

@media (min-width: 1024px) {
  .links-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: var(--spacing-xl);
  }
}

/* 链接卡片 - 玻璃态设计 */
.link-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background: var(--gradient-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  transition: all var(--transition-normal);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

/* 卡片悬停效果 */
.link-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at center, rgba(99, 102, 241, 0.1), transparent 70%);
  opacity: 0;
  transition: opacity var(--transition-normal);
}

.link-card:hover {
  transform: translateY(-4px);
  border-color: var(--color-border-hover);
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(99, 102, 241, 0.1);
}

.link-card:hover::before {
  opacity: 1;
}

.link-card:focus {
  outline: none;
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.3),
    0 0 0 2px var(--color-cta-mid);
}

.link-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  transition: all var(--transition-normal);
  position: relative;
  z-index: 1;
}

.link-card:hover .link-icon {
  background: var(--color-elevated);
  border-color: rgba(99, 102, 241, 0.2);
  transform: scale(1.05);
}

.icon-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: calc(var(--radius-md) - 2px);
  padding: 4px;
  opacity: 0.85;
}

.icon-placeholder {
  width: 100%;
  height: 100%;
  color: var(--color-text-muted);
  padding: 8px;
  opacity: 0.85;
}

.link-name {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  text-align: center;
  word-break: break-word;
  position: relative;
  z-index: 1;
  transition: color var(--transition-normal);
}

.link-card:hover .link-name {
  color: var(--color-text);
}

@media (max-width: 640px) {
  .homepage {
    padding: var(--spacing-lg) var(--spacing-md);
  }

  .floating-actions {
    top: var(--spacing-md);
    left: var(--spacing-md);
  }

  .floating-btn {
    width: 44px;
    height: 44px;
  }

  .floating-btn svg {
    width: 20px;
    height: 20px;
  }

  .links-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: var(--spacing-md);
  }

  .link-card {
    padding: var(--spacing-sm);
  }

  .link-card.edit-mode-active {
    padding-top: calc(var(--spacing-sm) + 16px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .spinner::before,
  .spinner::after {
    animation: none;
  }

  .link-card,
  .floating-btn,
  .edit-group-btn,
  .delete-group-btn,
  .retry-button,
  .link-edit-btn,
  .link-delete-btn {
    transition: none;
  }

  .link-card:hover,
  .floating-btn:hover {
    transform: none;
  }
}

/* 模态框遮罩 - 毛玻璃效果 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-md);
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* 模态框内容 - 高级玻璃态 */
.modal-content {
  background: var(--color-surface);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-xl);
  padding: var(--spacing-xl);
  width: 100%;
  max-width: 420px;
  box-shadow:
    var(--shadow-xl),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  animation: slideUp 0.3s ease;
  position: relative;
}

.modal-content::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: var(--radius-xl);
  background: var(--gradient-surface);
  pointer-events: none;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-title {
  font-family: 'Outfit', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: var(--spacing-lg);
  letter-spacing: -0.02em;
  position: relative;
  z-index: 1;
}

.add-group-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  position: relative;
  z-index: 1;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.form-field label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* 输入框样式 */
.form-field input {
  padding: 0.875rem var(--spacing-md);
  background: var(--color-background-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: 1rem;
  font-family: 'DM Sans', sans-serif;
  color: var(--color-text);
  transition: all var(--transition-normal);
}

.form-field input:focus {
  outline: none;
  border-color: var(--color-cta-mid);
  box-shadow:
    0 0 0 3px rgba(139, 92, 246, 0.1),
    0 0 20px rgba(139, 92, 246, 0.1);
  background: var(--color-primary);
}

.form-field input::placeholder {
  color: var(--color-text-muted);
}

.modal-actions {
  display: flex;
  gap: var(--spacing-sm);
  justify-content: flex-end;
  margin-top: var(--spacing-sm);
  position: relative;
  z-index: 1;
}

/* 基础按钮样式 */
.btn-cancel,
.btn-confirm,
.btn-delete {
  padding: 0.875rem 1.75rem;
  border-radius: var(--radius-lg);
  font-family: 'Outfit', sans-serif;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;
}

/* 取消按钮 - 次要样式 */
.btn-cancel {
  background: transparent;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
}

.btn-cancel:hover {
  background: var(--color-border);
  color: var(--color-text);
  border-color: var(--color-border-hover);
}

/* 确认按钮 - 主样式 */
.btn-confirm {
  background: var(--gradient-primary);
  background-size: 200% 200%;
  color: white;
  border: none;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
}

.btn-confirm:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(99, 102, 241, 0.4);
  background-position: 100% 0;
}

.btn-confirm:active:not(:disabled) {
  transform: translateY(0);
}

.btn-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* 删除按钮 - 危险操作强化 */
.btn-delete {
  padding: 0.875rem 1.75rem;
  background: linear-gradient(135deg, #EF4444, #DC2626);
  background-size: 200% 200%;
  color: white;
  border: none;
  font-family: 'Outfit', sans-serif;
  font-weight: 600;
  font-size: 0.875rem;
  border-radius: var(--radius-lg);
  box-shadow:
    0 4px 16px rgba(239, 68, 68, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;
}

.btn-delete::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at center, rgba(255, 255, 255, 0.2), transparent 70%);
  opacity: 0;
  transition: opacity var(--transition-normal);
}

.btn-delete:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow:
    0 8px 24px rgba(239, 68, 68, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
  background-position: 100% 0;
}

.btn-delete:hover:not(:disabled)::before {
  opacity: 1;
}

.btn-delete:active:not(:disabled) {
  transform: translateY(0);
}

.btn-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.delete-confirm-message {
  margin-bottom: var(--spacing-lg);
  color: var(--color-text-secondary);
  line-height: 1.6;
  position: relative;
  z-index: 1;
  padding: var(--spacing-md);
  background: var(--color-background-subtle);
  border-radius: var(--radius-md);
  border-left: 3px solid var(--color-danger);
}

.error-message {
  margin-top: var(--spacing-md);
  color: var(--color-danger);
  font-size: 0.875rem;
  text-align: center;
  background: rgba(239, 68, 68, 0.1);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-md);
  position: relative;
  z-index: 1;
}
</style>
